import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { signalsApi, outreachApi } from "@/lib/api";
import { applyJourneyToRnc } from "@/lib/rncJourney";
import { RncShell, RncCrumb, FlowCanvas } from "@/pages/signals/rnc/RncShared";
import { signalsDashboardPath, SIGNALS_TAB_RNC } from "@/pages/signals/signalsNav";

const AVATAR_COLORS = ["#4F46E5", "#16A34A", "#DC2626", "#3B82F6", "#14B8A6", "#F97316", "#0A66C2", "#7C3AED"];

function initialsFromName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "PR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function buildBaseRnc(payload, projectId) {
  const project = payload?.project || {};
  const name = project.project_name || "Untitled project";
  return {
    id: project.project_id || projectId,
    company: name,
    initials: initialsFromName(name),
    color: AVATAR_COLORS[0],
    channel: "rnc",
    campaignId: null,
    campaignName: null,
    workflowId: null,
    workflowVersion: null,
    workflowAssigned: false,
    signalWorkflow: null,
    status: null,
    progress: null,
    currentStep: null,
    stepIndex: 0,
  };
}

export default function RncWorkflowViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, journey: null, error: null });

  useEffect(() => {
    let cancelled = false;
    const projectId = decodeURIComponent(id || "").trim();
    setState({ loading: true, data: null, journey: null, error: null });
    Promise.all([
      signalsApi.getRmcProject(projectId),
      outreachApi.getRncJourney(projectId).catch(() => null),
    ])
      .then(([data, journey]) => {
        if (!cancelled) setState({ loading: false, data, journey, error: null });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            journey: null,
            error: e.message || "Failed to load workflow",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const rnc = useMemo(() => {
    if (!state.data) return null;
    const projectId = decodeURIComponent(id || "").trim();
    return applyJourneyToRnc(buildBaseRnc(state.data, projectId), state.journey);
  }, [state.data, state.journey, id]);

  if (state.loading) {
    return (
      <RncShell crumb={<RncCrumb company="Loading…" />}>
        <div className="card" style={{ padding: 20 }} data-testid="rnc-workflow-loading">
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </RncShell>
    );
  }

  if (state.error || !rnc) {
    return (
      <RncShell crumb={<RncCrumb company="Project" />}>
        <div className="card" style={{ padding: 20 }} data-testid="rnc-workflow-error">
          <p style={{ color: "#b91c1c", margin: 0 }}>{state.error || "Project not found"}</p>
          <p style={{ marginTop: 12 }}>
            <Link to={signalsDashboardPath(SIGNALS_TAB_RNC)} style={{ color: "var(--indigo)" }}>Return to RNC</Link>
          </p>
        </div>
      </RncShell>
    );
  }

  const assigned = Boolean(rnc.workflowAssigned);
  const wf = rnc.signalWorkflow;

  return (
    <RncShell
      crumb={(
        <>
          <RncCrumb company={rnc.company} />
          <span className="sep">/</span>
          <b>Workflow Viewer</b>
        </>
      )}
    >
      <div className="page-head">
        <div>
          <h1>
            {assigned && (wf?.name || rnc.campaignName) ? (wf?.name || rnc.campaignName) : "No Workflow Assigned"}
          </h1>
          <p>
            {assigned
              ? (
                <>
                  Read-only view of where <b>{rnc.company}</b> ({rnc.id}) currently sits inside this workflow.
                </>
              )
              : (
                <>
                  <b>{rnc.company}</b> ({rnc.id}) is pending workflow assignment. Auto-enroll starts when a channel default is set.
                </>
              )}
          </p>
        </div>
        <button type="button" className="btn whitespace-nowrap" onClick={() => navigate(`/signals/rnc/${encodeURIComponent(rnc.id)}`)} data-testid="back-to-rnc">
          <ArrowLeft size={14} /> Back to RNC Details
        </button>
      </div>
      <div className="legend">
        <span><span className="sw" style={{ background: "var(--green-600)" }} />Completed</span>
        <span><span className="sw" style={{ background: "var(--blue-500)" }} />Current step</span>
        <span><span className="sw" style={{ background: "var(--slate-300, #CBD5E1)" }} />Upcoming</span>
        <span style={{ marginLeft: "auto", color: "var(--slate-400)" }}>
          Campaign: {rnc.campaignName || "N/A"}
        </span>
      </div>
      <div className="builder-shell" style={{ gridTemplateColumns: "1fr" }}>
        <div className="canvas" style={{ padding: 34 }}>
          <FlowCanvas workflow={wf} progressIndex={assigned ? rnc.stepIndex : null} />
        </div>
      </div>
    </RncShell>
  );
}
